import type {
  AdjustBalanceResponse,
  BillingAnomalyResolvedFilter,
  GetAccountDetailResponse,
  ListBillingAnomaliesQuery,
  ListBillingAnomaliesResponse,
  ListAccountsQuery,
  ListAccountsResponse,
  MarkEmailVerifiedResponse,
  ResendEmailVerificationResponse,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers around `ops-api /v1/ops/admin/*`.
 *
 * Phase-1 contract for ops-initiated tenant admin:
 *   - List / inspect tenant accounts (email, org, balance, recent ledger).
 *   - Post signed balance adjustments (idempotent on
 *     `external_request_id`). Client always generates the
 *     `external_request_id` at modal mount so a double-click on the
 *     confirmation button dedupes downstream; ops-api also backstops with
 *     its own UUID when the client omits the field.
 *
 * Spoof-resistance: callers NEVER send `actor_user_id`. ops-api extracts
 * the operator id from the session cookie and injects it last-wins, so a
 * client-side spoof of `actor_user_id` is dropped.
 */

const BASE_PATH = '/v1/ops/admin';

function buildQueryString(query: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) {
      continue;
    }
    const stringValue = typeof value === 'number' ? value.toString() : value;
    if (stringValue.length === 0) {
      continue;
    }
    params.set(key, stringValue);
  }
  const s = params.toString();
  return s.length > 0 ? `?${s}` : '';
}

// ─── Accounts list ───────────────────────────────────────────────────────

export async function listAccounts(
  query: ListAccountsQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListAccountsResponse> {
  const qs = buildQueryString({
    limit: query.limit,
    offset: query.offset,
    email: query.email,
    hasBalance: query.hasBalance,
    lowBalance: query.lowBalance,
    sort: query.sort,
  });
  return opsApiRequest<ListAccountsResponse>(
    `${BASE_PATH}/accounts${qs}`,
    { signal },
    fetchImpl,
  );
}

// ─── Account detail ──────────────────────────────────────────────────────

export async function getAccount(
  accountId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetAccountDetailResponse> {
  return opsApiRequest<GetAccountDetailResponse>(
    `${BASE_PATH}/accounts/${encodeURIComponent(accountId)}`,
    { signal },
    fetchImpl,
  );
}

// ─── Balance adjustments ─────────────────────────────────────────────────

/**
 * Client payload for adjusting an account's balance.
 *
 * `amount_usd_cents` may be negative (negative = debit; allowed to drive balance
 * below zero by design). `external_request_id` is required client-side so
 * a double-submit dedupes; the modal mints a fresh UUID on open.
 *
 * `actor_user_id` is deliberately absent — ops-api injects the
 * session-derived id and drops any client-supplied value.
 */
export interface AdjustBalanceClientRequest {
  readonly amount_usd_cents: number;
  readonly reason: string;
  readonly external_request_id: string;
  readonly metadata?: Record<string, unknown>;
}

export async function adjustBalance(
  accountId: string,
  body: AdjustBalanceClientRequest,
  fetchImpl: typeof fetch,
): Promise<AdjustBalanceResponse> {
  return opsApiRequest<AdjustBalanceResponse>(
    `${BASE_PATH}/accounts/${encodeURIComponent(accountId)}/balance-adjustments`,
    { method: 'POST', body },
    fetchImpl,
  );
}

// ─── Email verification support actions ─────────────────────────────────

export interface ResendEmailVerificationClientRequest {
  readonly reason?: string;
}

export async function resendEmailVerification(
  accountId: string,
  body: ResendEmailVerificationClientRequest,
  fetchImpl: typeof fetch,
): Promise<ResendEmailVerificationResponse> {
  return opsApiRequest<ResendEmailVerificationResponse>(
    `${BASE_PATH}/accounts/${encodeURIComponent(accountId)}/email-verification/resend`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export interface MarkEmailVerifiedClientRequest {
  readonly reason: string;
}

export async function markEmailVerified(
  accountId: string,
  body: MarkEmailVerifiedClientRequest,
  fetchImpl: typeof fetch,
): Promise<MarkEmailVerifiedResponse> {
  return opsApiRequest<MarkEmailVerifiedResponse>(
    `${BASE_PATH}/accounts/${encodeURIComponent(accountId)}/email-verification/mark-verified`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function listBillingAnomalies(
  query: ListBillingAnomaliesQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListBillingAnomaliesResponse> {
  const qs = buildQueryString({
    resolvedFilter: query.resolvedFilter,
    limit: query.limit,
    offset: query.offset,
  });
  return opsApiRequest<ListBillingAnomaliesResponse>(
    `${BASE_PATH}/billing-anomalies${qs}`,
    { signal },
    fetchImpl,
  );
}

export type { BillingAnomalyResolvedFilter };

// ─── Account lifecycle (suspend / unsuspend / soft-delete) ──────────────
// Epic #109 / P2.10. Ops-admin-only mutations; backend writes an
// `ops_audit` row for each success (target_type='account').

export interface SuspendAccountClientRequest {
  readonly reason?: string;
}

export interface SuspendAccountResult {
  readonly account_id: string;
  readonly suspended_at: string;
  readonly suspension_reason: string | null;
}

export async function suspendAccount(
  accountId: string,
  body: SuspendAccountClientRequest,
  fetchImpl: typeof fetch,
): Promise<SuspendAccountResult> {
  return opsApiRequest<SuspendAccountResult>(
    `${BASE_PATH}/accounts/${encodeURIComponent(accountId)}/suspend`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export interface UnsuspendAccountResult {
  readonly account_id: string;
  readonly unsuspended_at: string;
}

export async function unsuspendAccount(
  accountId: string,
  fetchImpl: typeof fetch,
): Promise<UnsuspendAccountResult> {
  return opsApiRequest<UnsuspendAccountResult>(
    `${BASE_PATH}/accounts/${encodeURIComponent(accountId)}/unsuspend`,
    { method: 'POST', body: {} },
    fetchImpl,
  );
}

export interface SoftDeleteAccountResult {
  readonly account_id: string;
  readonly deleted_at: string;
}

export async function softDeleteAccount(
  accountId: string,
  fetchImpl: typeof fetch,
): Promise<SoftDeleteAccountResult> {
  return opsApiRequest<SoftDeleteAccountResult>(
    `${BASE_PATH}/accounts/${encodeURIComponent(accountId)}/soft-delete`,
    { method: 'POST', body: {} },
    fetchImpl,
  );
}
